/**
 * Client first-run onboarding tour (WEB-156, R1.4 sub-step 5).
 *
 * Mirror of macOS MAC-157 (`Services/TourState.swift`). A 4-step modal
 * overlay introducing the Hero Dashboard / Daily check-in / Tools /
 * Settings, shown once per user-id. Skippable; replay-able from
 * Settings.
 *
 * Persistence: `localStorage.h3eromap.tour-completed-v1.<userId>`.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_PREFIX = 'h3eromap.tour-completed-v1.';

const STEPS = ['welcome', 'checkIn', 'tools', 'settings'] as const;
type Step = (typeof STEPS)[number];

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function isClientTourCompleted(userId: string): boolean {
  try {
    return window.localStorage.getItem(storageKey(userId)) === 'true';
  } catch {
    return false;
  }
}

export function markClientTourCompleted(userId: string): void {
  try {
    window.localStorage.setItem(storageKey(userId), 'true');
  } catch {
    /* swallow */
  }
}

export function resetClientTour(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    /* swallow */
  }
}

interface ClientTourProps {
  userId: string;
  /** When `forceShow` is `true`, the tour opens regardless of the
   *  completion flag (for the Settings → "Replay tour" CTA). */
  forceShow?: boolean;
  /** Called when the tour is dismissed (skipped / completed / replayed). */
  onClose?: () => void;
}

export function ClientTour({
  userId,
  forceShow = false,
  onClose,
}: ClientTourProps): JSX.Element | null {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  /* On mount, decide whether to show. */
  useEffect(() => {
    if (forceShow) {
      setIsOpen(true);
      setStepIndex(0);
      return;
    }
    if (!isClientTourCompleted(userId)) {
      setIsOpen(true);
      setStepIndex(0);
    }
  }, [userId, forceShow]);

  const handleClose = useCallback(
    (markComplete: boolean) => {
      if (markComplete) markClientTourCompleted(userId);
      setIsOpen(false);
      onClose?.();
    },
    [userId, onClose],
  );

  const next = useCallback(() => {
    if (stepIndex >= STEPS.length - 1) {
      handleClose(true);
    } else {
      setStepIndex((prev) => prev + 1);
    }
  }, [stepIndex, handleClose]);

  if (!isOpen) return null;

  const step: Step = STEPS[stepIndex] ?? STEPS[0];
  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-tour-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-s4"
      data-testid="client-tour"
    >
      <article
        className="bg-bone rounded-lg shadow-xl border border-bone-3 max-w-md w-full p-s6"
      >
        <p
          className="font-mono text-xs uppercase tracking-wide text-gray-1"
          data-testid="client-tour-step-indicator"
        >
          {t('tour.client.stepIndicator', { current: stepIndex + 1, total: STEPS.length })}
        </p>
        <h2
          id="client-tour-title"
          className="font-display text-display-md text-ink mt-s2"
          data-testid="client-tour-title"
        >
          {t(`tour.client.${step}.title`)}
        </h2>
        <p className="mt-s3 text-gray-2" data-testid="client-tour-body">
          {t(`tour.client.${step}.body`)}
        </p>

        <div className="mt-s5 flex gap-s3 justify-end">
          {!isLast && (
            <button
              type="button"
              onClick={() => handleClose(true)}
              className="btn-ghost text-sm"
              data-testid="client-tour-skip"
            >
              {t('tour.client.skip')}
            </button>
          )}
          <button
            type="button"
            onClick={next}
            className="btn-primary text-sm"
            data-testid="client-tour-next"
          >
            {isLast ? t('tour.client.gotIt') : t('tour.client.next')}
          </button>
        </div>
      </article>
    </div>
  );
}
